import { isEntryEditable, type Entry, type Fixture, type Prediction, type Round } from '@h2h/shared';

type RouteOpts = {
  roundId: string | null;
  round?: Round | null;
  entry?: Entry | null;
  now?: number;
};

function roundQs(roundId: string | null, extra?: Record<string, string>): string {
  const q = new URLSearchParams();
  if (roundId) q.set('round', roundId);
  if (extra) for (const [k, v] of Object.entries(extra)) q.set(k, v);
  const s = q.toString();
  return s ? `?${s}` : '';
}

function fixtureStarted(fixture: Fixture, now: number): boolean {
  return fixture.status !== 'SCHEDULED' || fixture.kickoffAt <= now;
}

/**
 * Where a football fixture tap should land.
 *
 * Build-Your-Card keeps a card editable until its OWN kickoff (FLEXIBLE) or the round's
 * first kickoff (EXACT). So an *editable* fixture — whether it already has a saved card
 * or not — opens the card BUILDER: a fresh card if none exists, or the saved card
 * pre-filled (the make-picks flow seeds drafts from the entry) so the player can re-pick
 * the base or re-boost it. That's exactly what the EDITABLE badge promises.
 *
 * Once the fixture has kicked off / gone live / settled (or the entry is fully locked),
 * the tap opens the read-only detail instead — your card + live grade + match stats.
 */
export function footballFixtureHref(
  leagueId: string,
  fixture: Fixture,
  opts: RouteOpts & { myPick?: Prediction }
): string {
  const now = opts.now ?? Date.now();
  const round = opts.round;
  const started = fixtureStarted(fixture, now);

  // A card on this fixture can still be built or changed when the round is taking
  // entries, the entry isn't fully locked (EXACT: round's first kickoff; FLEXIBLE: while
  // OPEN/LOCKING), and THIS fixture hasn't kicked off yet.
  const editable = !!round && !started && isEntryEditable(round, [], now);
  if (editable) {
    return footballHubPickHref(leagueId, { roundId: opts.roundId, fixtureId: fixture.fixtureId });
  }

  return `/play/${leagueId}/fixture/${fixture.fixtureId}${roundQs(opts.roundId)}`;
}

/** Where a cycling duel tap should land — pick flow pre-stage, detail once racing or called. */
export function cyclingDuelHref(leagueId: string, fixture: Fixture, opts: RouteOpts): string {
  const now = opts.now ?? Date.now();
  const hasCall = !!opts.entry?.duelPicks?.some((p) => p.duelId === fixture.fixtureId);
  const settled = fixture.status === 'SETTLED' || fixture.status === 'VOID';
  const racing = fixture.status === 'LIVE' || !!(opts.round && !isEntryEditable(opts.round, [], now));

  if (hasCall || settled || racing) {
    return `/play/${leagueId}/fixture/${fixture.fixtureId}${roundQs(opts.roundId)}`;
  }

  if (opts.round && isEntryEditable(opts.round, [], now)) {
    return cyclingHubPickHref(leagueId, { roundId: opts.roundId, duelId: fixture.fixtureId });
  }

  return `/play/${leagueId}/fixture/${fixture.fixtureId}${roundQs(opts.roundId)}`;
}

/** League hub (Compete tab) with an optional duel deep-link that scrolls to the card. */
export function cyclingHubPickHref(
  leagueId: string,
  opts: { roundId?: string | null; duelId?: string }
): string {
  const extra = opts.duelId ? { duel: opts.duelId } : undefined;
  return `/play/${leagueId}${roundQs(opts.roundId ?? null, extra)}`;
}

/** League hub (Compete tab) with an optional fixture deep-link that auto-opens the builder. */
export function footballHubPickHref(
  leagueId: string,
  opts: { roundId?: string | null; fixtureId?: string }
): string {
  const extra = opts.fixtureId ? { fixture: opts.fixtureId } : undefined;
  return `/play/${leagueId}${roundQs(opts.roundId ?? null, extra)}`;
}

export function fixtureHref(
  leagueId: string,
  fixture: Fixture,
  sport: 'FOOTBALL' | 'CYCLING',
  opts: RouteOpts & { myPick?: Prediction }
): string {
  return sport === 'CYCLING' ? cyclingDuelHref(leagueId, fixture, opts) : footballFixtureHref(leagueId, fixture, opts);
}
